import { BACKEND_URL } from '@/config/api';
/**
 * EngagementLevelModal - Phase III-B Engagement Gate
 * 
 * SYSTEM CONTRACT:
 * - Blocking modal overlay that MUST be resolved before checkout
 * - Activated only when a monetized action is attempted
 * - No page redirect, no background interaction allowed
 * 
 * GUARDRAILS:
 * - No inference from existing credits
 * - No silent defaults to subscriber pricing
 * - No Stripe checkout without engagement level persisted
 * - Engagement level must be explicit, stored, and referenced by checkout
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, CreditCard, BookOpen, Star, Crown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Engagement level options matching NFM catalog exactly
const ENGAGEMENT_OPTIONS = [
  {
    value: 'alacarte',
    label: 'A La Carte',
    description: 'Pay as you go. No subscription, no commitment.',
    icon: CreditCard,
    isSubscription: false,
    color: 'text-slate-400',
    bgColor: 'bg-slate-800/50',
    borderColor: 'border-slate-700',
  },
  {
    value: 'novice',
    label: 'Novice',
    description: 'Step into stories regularly. 350 credits/month.',
    icon: BookOpen,
    isSubscription: true,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-900/20',
    borderColor: 'border-emerald-500/30',
  },
  {
    value: 'pro-reader',
    label: 'Pro Reader',
    description: 'Explore multiple worlds. 750 credits/month.',
    icon: Star,
    isSubscription: true,
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/20',
    borderColor: 'border-purple-500/30',
  },
  {
    value: 'book-worm',
    label: 'Book Worm',
    description: 'Deep immersion, maximum value. 1,250 credits/month.',
    icon: Crown,
    isSubscription: true,
    color: 'text-amber-400',
    bgColor: 'bg-amber-900/20',
    borderColor: 'border-amber-500/30',
  },
];

const CADENCE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual', badge: 'Save 1 month' },
];

export default function EngagementLevelModal({
  isOpen,
  onClose,
  onSuccess,
  userId,
  token,
}) {
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedCadence, setSelectedCadence] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedOption = ENGAGEMENT_OPTIONS.find(opt => opt.value === selectedLevel);
  const requiresCadence = selectedOption?.isSubscription;

  const handleSubmit = async () => {
    if (!selectedLevel) {
      setError('Please select an engagement level');
      return;
    }

    if (requiresCadence && !selectedCadence) {
      setError('Please select a billing cadence');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/nfm/engagement/set?user_id=${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({
            engagement_level: selectedLevel,
            cadence: requiresCadence ? selectedCadence : null,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to set engagement level');
      }

      const result = await response.json();
      
      toast.success(`Engagement level set to ${selectedOption.label}`);
      
      // Call success callback with the result
      if (onSuccess) {
        onSuccess({
          engagementLevel: result.engagement_level,
          cadence: result.cadence,
          isSubscriber: result.is_subscriber,
        });
      }
      
      onClose();
      
    } catch (err) {
      console.error('[ENGAGEMENT] Error setting level:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedLevel(null);
      setSelectedCadence('monthly');
      setError(null);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-lg bg-slate-900 border-slate-700 text-slate-50 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="engagement-level-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white">
            Choose Your Engagement Level
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Your pricing depends on your engagement level. Please select how you'd like to explore NewAudia.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Explanation */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-300">
              <strong className="text-white">Why is this required?</strong><br />
              Pricing is determined by your engagement level. Subscribers get better rates on credit bundles.
            </p>
          </div>

          {/* Engagement Level Selection */}
          <RadioGroup
            value={selectedLevel}
            onValueChange={setSelectedLevel}
            className="space-y-3"
          >
            {ENGAGEMENT_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedLevel === option.value;
              
              return (
                <div
                  key={option.value}
                  className={`relative rounded-lg border p-4 cursor-pointer transition-all ${
                    isSelected
                      ? `${option.bgColor} ${option.borderColor} border-2`
                      : 'bg-slate-800/30 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => setSelectedLevel(option.value)}
                  data-testid={`engagement-option-${option.value}`}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                      className="mt-1 border-slate-600 text-purple-500"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={option.value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Icon className={`w-5 h-5 ${option.color}`} />
                        <span className="font-semibold text-white">{option.label}</span>
                        {option.isSubscription && (
                          <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full">
                            Subscription
                          </span>
                        )}
                      </Label>
                      <p className="text-sm text-slate-400 mt-1 ml-7">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </RadioGroup>

          {/* Cadence Selection (only for subscriptions) */}
          {requiresCadence && (
            <div className="pt-4 border-t border-slate-700">
              <p className="text-sm font-medium text-slate-300 mb-3">
                Billing Cadence
              </p>
              <RadioGroup
                value={selectedCadence}
                onValueChange={setSelectedCadence}
                className="flex gap-4"
              >
                {CADENCE_OPTIONS.map((cadence) => (
                  <div
                    key={cadence.value}
                    className={`flex-1 rounded-lg border p-3 cursor-pointer transition-all ${
                      selectedCadence === cadence.value
                        ? 'bg-purple-900/20 border-purple-500/50'
                        : 'bg-slate-800/30 border-slate-700 hover:border-slate-600'
                    }`}
                    onClick={() => setSelectedCadence(cadence.value)}
                    data-testid={`cadence-option-${cadence.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value={cadence.value}
                        id={`cadence-${cadence.value}`}
                        className="border-slate-600 text-purple-500"
                      />
                      <Label
                        htmlFor={`cadence-${cadence.value}`}
                        className="cursor-pointer font-medium text-white"
                      >
                        {cadence.label}
                      </Label>
                      {cadence.badge && (
                        <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">
                          {cadence.badge}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="engagement-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !selectedLevel}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
            data-testid="engagement-confirm-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue to Checkout'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
